// FORECAST STABILITY — aggregating the shift-judge verdict across a contract's fires.
//
// WHY AN AGGREGATE AND NOT A BADGE. The shift judge is ~5% unstable run-to-run at temperature 0,
// so a single label on a single move is close to noise: "5 of the last 8 significant changes in
// the forecast were explained by new information" is defensible where a red badge on one move is
// not. That is a product constraint, not a rendering preference — a per-forecast "unwarranted"
// stamp would over-claim on an unstable label AND, because the unexplained class demonstrably
// captures retrieval nondeterminism (the same market returning different prices across locale
// mirrors), it would blame the model for an infrastructure defect.
//
// NO INTERNAL STRUCTURE REACHES THE USER. There is ONE forecasting agent as far as this surface
// is concerned. Which internal lane/run/class produced a number is an implementation detail, and
// it answers a question the user never asked — so this module derives its user-facing strings
// from the three DISPLAY KINDS alone and never from a lane name, a run tag, or a judge class
// string. Prose stays in the AUDITOR'S VOICE: a third party saying what changed, not the agent
// narrating its own findings.
//
// THREE FILTERS, ALL LOAD-BEARING — the whole reason this is a tested module rather than an
// inline `.filter()`:
//
//   1. `shift.gated` — a gated verdict was derived arithmetically because the probability
//      barely moved (|Δp| under the judge's gate), so it is not a MOVE at all. Pooling gated
//      rows with ruled ones inflates the explained share with non-events; on the live roster
//      roughly two thirds of transitions are gated, so this is the difference between a
//      meaningful rate and a flattering one.
//   2. `shift.reasonClass === "no_shift"` — the judge reaching, on its own, the same verdict
//      the gate reaches arithmetically: not a material move. A non-move does not belong in a
//      rate OF moves, and WHICH of the two decided it is an artifact of how much we were
//      willing to spend, not a fact about the forecast. Every gated row is also `no_shift`,
//      so filter 1 is strictly redundant with this one — it is kept because its reason
//      (never judged, two thirds of traffic) is a different claim worth stating separately.
//      Unlike filter 3 this is NOT disclosed: there is nothing unassessed about it, we
//      simply know it was not a move.
//   3. `shift.reasonClass === "judge_error"` — the judge could not rule, so there is no verdict
//      to pool. Those fires are DISCLOSED (the `unassessed` count drives a visible line) rather
//      than silently absorbed: dropping them from both the denominator and the copy would
//      present a cleaner rate than the data supports.
import { fmtPct, leadingSide, sideChanceText } from "./formatPct.js";

/**
 * The three shapes every displayed move takes, mapped to the short user-language reason the
 * expanded detail shows. Derived from the kind ALONE — never from an internal class string.
 *
 *   evidence       — something new came in.
 *   interpretation — the resolution criteria are being read differently.
 *   reevaluation   — no new information; the model changed its evaluation of what it already had.
 */
const REASON_BY_KIND = {
  evidence: "cited new information",
  interpretation: "read the resolution criteria differently",
  reevaluation: "re-evaluated the evidence already in hand",
};

/** The display kinds this UI knows how to render. Anything else is treated as absent. */
export const SHIFT_DISPLAY_KINDS = Object.freeze(Object.keys(REASON_BY_KIND));

/**
 * The user-language reason for a move of the given display kind.
 *
 * An absent/unrecognized kind is NOT guessed at: a move can be ruled unexplained while the
 * display block is missing (a kind is only written for a displayable verdict), and inventing a
 * reason there would fabricate a finding. Say there is none instead.
 *
 * @param {string|null|undefined} kind a `shift_display_kind` value
 * @returns {string}
 */
export function stabilityReason(kind) {
  return REASON_BY_KIND[kind] || "no reason recorded";
}

// Pinned month abbreviations rather than `toLocaleDateString`: the detail line is a compact
// "31 Jul" that must not reflow into "Jul 31" / "31/07" depending on the viewer's locale, and
// the surrounding copy is English-only anyway.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A fire timestamp as the compact LOCAL "31 Jul" the detail list uses, or null when it can't be
 * parsed (the line then renders without a date rather than showing "Invalid Date").
 *
 * @param {string|null|undefined} iso a fire's ISO timestamp
 * @returns {string|null}
 */
export function formatStabilityDate(iso) {
  if (typeof iso !== "string" || iso.length === 0) return null;
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/**
 * The probability move as "72% Yes → 80% Yes", each end by its larger side, or null when either
 * end is unknown (a half-known move would be more confusing than none).
 *
 * @param {number|null|undefined} prev the prior fire's probability, 0..1
 * @param {number|null|undefined} cur this fire's probability, 0..1
 * @returns {string|null}
 */
export function formatStabilityMove(prev, cur) {
  const ok = (v) => typeof v === "number" && Number.isFinite(v);
  if (!ok(prev) || !ok(cur)) return null;
  const [a, b] = [leadingSide(prev), leadingSide(cur)];
  return `${fmtPct(a.p)}% ${a.label} → ${fmtPct(b.p)}% ${b.label}`;
}

/**
 * One unexplained change as its rendered detail line, e.g.
 * `31 Jul · 72% → 80% — re-evaluated the evidence already in hand`.
 *
 * Composed here (not in the component) so the exact user-facing string is unit-testable. The
 * date and the move are each omitted when unknown, so a line degrades to just the reason rather
 * than rendering an empty segment or a placeholder dash.
 *
 * @param {{date: string|null, move: string|null, reason: string}} example
 * @returns {string}
 */
export function stabilityDetailLine({ date, move, reason }) {
  const head = [date, move].filter(Boolean).join(" · ");
  return head ? `${head} — ${reason}` : reason;
}

/**
 * The render model for the WHAT CHANGED SINCE LAST FORECAST box, or `null` when the fire has
 * nothing displayable to say.
 *
 * ALL THREE KINDS RENDER, including the unflattering ones. The box used to appear only for a
 * verdict the judge called warranted, which meant a move it called unwarranted produced SILENCE
 * — the number changed and the user got no account of it at all. The honest replacement is the
 * `reevaluation` sentence. Absence is reserved for fires that genuinely have no displayable
 * verdict, and it must never read as "unwarranted".
 *
 * `text` is passed through UNTOUCHED. The backend writes it in the auditor's voice with no
 * internals in it; any re-wording here would be the UI editorializing over a judged finding.
 *
 * Both ends are formatted here, each by its larger side ("72% chance of Yes"), so the
 * component holds no arithmetic. `prevText` is null when the prior probability is unknown, and
 * `curText` when this one is (the "Last forecast → Current" line is then omitted rather than
 * showing a placeholder).
 *
 * @param {object|null|undefined} display the parsed `shift_display_*` block
 * @param {object|null|undefined} shift the parsed `shift_*` verdict (for `prevProbability`)
 * @param {number} probability this fire's probability, 0..1
 * @returns {{kind: string, text: string, readBefore: string|null, readAfter: string|null,
 *   prevText: string|null, curText: string|null}|null}
 */
export function shiftChangeBlock(display, shift, probability) {
  if (!display || !SHIFT_DISPLAY_KINDS.includes(display.kind)) return null;
  // A kind with no text has nothing to say. The contract guarantees the two travel together, so
  // this only fires on a malformed row — and an empty box would be worse than none.
  if (typeof display.text !== "string" || display.text.length === 0) return null;
  const isInterpretation = display.kind === "interpretation";
  const prev = shift && shift.prevProbability;
  return {
    kind: display.kind,
    text: display.text,
    // The two readings ARE the change for an `interpretation`, so they are shown side by side
    // rather than paraphrased into the sentence. Never shown for the other kinds, where they
    // would sit under a heading the kind does not mean.
    readBefore: isInterpretation ? display.readBefore || null : null,
    readAfter: isInterpretation ? display.readAfter || null : null,
    prevText: typeof prev === "number" && Number.isFinite(prev) ? sideChanceText(prev) : null,
    curText:
      typeof probability === "number" && Number.isFinite(probability)
        ? sideChanceText(probability)
        : null,
  };
}

/**
 * Summarize the shift verdicts across a contract's fires.
 *
 * @param {Array<{
 *   fireTs?: string|null,
 *   shift?: object|null,
 *   display?: object|null,
 *   probability?: number|null,
 * }>} fires
 *   one entry per fire, order irrelevant (this is a count, not a series).
 * @returns {{
 *   moves: number,        // ruled moves the judge called material — the aggregate's denominator
 *   unexplained: number,  // of those, the ones the judge ruled `justified === false`
 *   unassessed: number,   // ruled moves the judge errored on — disclosed, not absorbed
 *   examples: Array<{fireTs: string|null, date: string|null, move: string|null, reason: string}>,
 * } | null}
 *   `null` when there is nothing to say — no fire carried a verdict at all. That is the EMPTY
 *   STATE, and it is mandatory rather than a nicety: there is no verdict on a contract's first
 *   fire on its own chain. A caller must render NOTHING, never "unwarranted" — absence must
 *   never read as a negative finding.
 *
 *   `moves === 0` with `unassessed > 0` is a distinct, real state: every move was a move but
 *   none could be assessed. Also not an aggregate — say so rather than reporting "0 of 0".
 *
 *   `moves === 0` with `unassessed === 0` is a THIRD state, and the most ordinary of the three:
 *   fires carried verdicts, but every one was gated or ruled `no_shift`. Everything WAS assessed
 *   and none of it was a material move — a quiet contract. A caller must not report that as
 *   missing history; there is nothing to rate because nothing happened.
 */
export function summarizeShiftStability(fires) {
  if (!Array.isArray(fires)) return null;
  let ruled = 0;
  let moves = 0;
  let unexplained = 0;
  let unassessed = 0;
  const examples = [];
  for (const fire of fires) {
    const shift = fire && fire.shift;
    if (!shift) continue;
    ruled += 1;
    // Filter 1: a gated verdict is a non-move (derived arithmetically, not ruled).
    if (shift.gated === true) continue;
    // Filter 2: the judge itself said this was not a material move. Same conclusion as the
    // gate, reached above the threshold instead of below it — and a non-move is a non-move
    // whichever way it was decided. Not disclosed as unassessed: it WAS assessed.
    if (shift.reasonClass === "no_shift") continue;
    // Filter 3: the judge errored — no verdict exists to pool. Disclosed separately.
    if (shift.reasonClass === "judge_error") {
      unassessed += 1;
      continue;
    }
    // A ruled verdict always carries a boolean; guard anyway so a malformed row cannot be
    // counted as explained (it is disclosed as unassessed instead).
    if (typeof shift.justified !== "boolean") {
      unassessed += 1;
      continue;
    }
    moves += 1;
    if (shift.justified) continue;
    unexplained += 1;
    const fireTs = typeof fire.fireTs === "string" ? fire.fireTs : null;
    examples.push({
      fireTs,
      date: formatStabilityDate(fireTs),
      move: formatStabilityMove(shift.prevProbability, fire.probability),
      reason: stabilityReason(fire.display && fire.display.kind),
    });
  }
  if (ruled === 0) return null;
  return { moves, unexplained, unassessed, examples };
}
